import {
  doc, updateDoc, addDoc, collection,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';
import { assignTerritory } from '../../utils/pointInPolygon';

// Permanent delete — server-side callable only (rules keep client deletes at
// `allow delete: if false`). Gated owner/hqadmin; writes a full-snapshot audit
// row before removing the lead + its notes. Everyone else archives instead.
export function adminDeleteLead(leadId) {
  return httpsCallable(functions, 'adminDeleteLead')({ leadId }).then((r) => r.data);
}

// Audited lead-stage change (Kanban drag, etc.). Stamps the actor so onLeadWrite
// records WHO moved the deal (DoR-27b). The audit row is written server-side.
export function updateLeadStage(leadId, stage, actor) {
  return updateDoc(doc(db, 'leads', leadId), {
    stage,
    updatedBy: actor?.uid ?? null,
    updatedByName: actor?.name ?? null,
    updatedAt: Date.now(),
  });
}

// General audited lead update (Lead Drawer "Save"). `patch` carries only the
// changed business fields; onLeadWrite diffs + appends one auditLogs row each.
export function updateLead(leadId, patch, actor) {
  return updateDoc(doc(db, 'leads', leadId), {
    ...patch,
    updatedBy: actor?.uid ?? null,
    updatedByName: actor?.name ?? null,
    updatedAt: Date.now(),
  });
}

// Verified toggle for "New" leads (CRM revisions Task 2). Writes the `verified`
// flag through the audited path so onLeadWrite logs the change (actor, old→new).
export function setLeadVerified(leadId, verified, actor) {
  return updateLead(leadId, { verified: !!verified }, actor);
}

// Archive / un-archive a Won or Lost lead (CRM revisions Task 2). Archived leads
// drop off the active board/table; the change is audited via onLeadWrite.
export function setLeadArchived(leadId, archived, actor) {
  return updateLead(leadId, { archived: !!archived }, actor);
}

// Build a new-lead doc body: stamp tenant/store, coerce numerics, assign the
// territory by point-in-polygon when lat/lng given (else use the chosen one).
// Returns the object to write; pure so it can be unit-tested.
export function buildNewLead(input, { tenantId, storeId, territories = [] }) {
  const lat = input.lat != null && input.lat !== '' ? Number(input.lat) : null;
  const lng = input.lng != null && input.lng !== '' ? Number(input.lng) : null;
  const territoryId = input.territoryId
    || (lat != null && lng != null ? assignTerritory([lat, lng], territories) : null);
  const now = Date.now();
  return {
    tenantId,
    storeId: storeId ?? null,
    territoryId,
    businessName: input.businessName ?? '',
    category: input.category ?? null,
    tier: input.tier ?? 'Small',
    stage: input.stage ?? 'New',
    leadScore: Number(input.leadScore) || 0,
    // Monthly peso value (CRM revisions Task 4 — replaces est. kg/mo).
    leadValue: Number(input.leadValue) || 0,
    // New-stage Verified sub-status (CRM revisions Task 2).
    verified: input.verified ?? false,
    // Recurring-service coverage days (CRM revisions Task 3).
    coverageDays: Array.isArray(input.coverageDays) ? input.coverageDays : [],
    archived: false,
    area: input.area ?? null,
    contactPerson: input.contactPerson ?? null,
    phone: input.phone ?? null,
    nextAction: input.nextAction ?? null,
    nextDate: input.nextDate ? new Date(input.nextDate).getTime() : null,
    // Per-lead owner (T-U3-003): the salesman this lead is attributed to. Used
    // for the manager "filter by salesman"; rules scoping stays territory-based.
    ownerUid: input.ownerUid ?? null,
    ownerName: input.ownerName ?? null,
    lat,
    lng,
    lifecycle: 'prospect',
    createdAt: now,
    updatedAt: now,
  };
}

// Create a new lead (manual "New lead"). Territory must be in the actor's scope
// or the rules (leadScope) reject the write — surfaced to the caller.
export function createLead(input, { tenantId, storeId, territories = [] }, actor) {
  const body = buildNewLead(input, { tenantId, storeId, territories });
  return addDoc(collection(db, 'leads'), {
    ...body,
    // Default the owner to the creating actor when the form didn't pick one.
    ownerUid: body.ownerUid ?? actor?.uid ?? null,
    ownerName: body.ownerName ?? actor?.name ?? null,
    createdBy: actor?.uid ?? null,
    createdByName: actor?.name ?? null,
    updatedBy: actor?.uid ?? null,
    updatedByName: actor?.name ?? null,
  });
}
